import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { searchPhotos } from "../lib/api";

const FEATURES = [
  {
    key: "curated",
    title: "Curated Frames",
    desc: "High-conversion visuals designed for global campaigns. Vetted for brand safety and legal indemnification.",
    bgQuery: "luxury lifestyle campaign photography",
    navQuery: "curated campaign visuals",
  },
  {
    key: "motions",
    title: "Motions",
    desc: "Production-grade 4K and HD footage. Elevate your storytelling with world-class motion assets from the industry's premier vaults.",
    bgQuery: "cinematic motion blur film still",
    navQuery: "cinematic motion footage stills",
  },
  {
    key: "editorials",
    title: "Editorials",
    desc: "Unfiltered access to the world's most significant moments. Authentic reporting and archival depth for definitive storytelling.",
    bgQuery: "photojournalism reportage",
    navQuery: "editorial photojournalism",
  },
];

export default function FeatureContainers() {
  const [bgs, setBgs] = useState({});

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      FEATURES.map((f) =>
        searchPhotos({ query: f.bgQuery, per_page: 1, page: 1 })
          .then((d) => [f.key, d.photos?.[0] || null])
          .catch(() => [f.key, null])
      )
    ).then((pairs) => {
      if (cancelled) return;
      setBgs(Object.fromEntries(pairs));
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      data-testid="feature-containers"
      className="max-w-[1600px] mx-auto px-6 md:px-10 mt-8 md:mt-10"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {FEATURES.map((f) => {
          const bg = bgs[f.key];
          const to = f.key === "curated" ? "/dashboard/curated-frames" : `/dashboard/search?q=${encodeURIComponent(f.navQuery)}`;
          return (
            <Link
              key={f.key}
              to={to}
              data-testid={`feature-${f.key}`}
              className="group block"
            >
              {/* Image card with heading pinned to top */}
              <div className="relative overflow-hidden bg-ink shadow-sm transition-all duration-500 group-hover:-translate-y-1 group-hover:shadow-xl" style={{ aspectRatio: "5 / 4" }}>
                {bg?.src && (
                  <img
                    src={bg.src.large || bg.src.medium}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                    style={{ backgroundColor: bg.avg_color || "#111" }}
                  />
                )}
                {/* Overlay — darker at top so the heading sits crisp over image */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/20 to-black/10" />
                {/* Heading at TOP of the image */}
                <div className="absolute inset-x-0 top-0 p-6 md:p-8">
                  <div className="text-[10px] uppercase tracking-[0.35em] text-gold mb-1.5">— {String(FEATURES.indexOf(f) + 1).padStart(2, "0")}</div>
                  <h3 className="font-serif text-ivory text-3xl md:text-4xl lg:text-5xl leading-[1.05] drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)]">{f.title}</h3>
                </div>
              </div>

              {/* Description + CTA BELOW the image */}
              <div className="mt-4">
                <p className="text-sm md:text-[15px] text-muted-foreground leading-relaxed">{f.desc}</p>
                <div data-testid={`feature-${f.key}-cta`} className="mt-3 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-gold">
                  <span className="es-gold-underline">Explore</span>
                  <span aria-hidden className="transition-transform duration-300 group-hover:translate-x-1.5">→</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
